<!-- ============================================================
     VIEW AREA / PROPERTY DETAIL
     Natural document scrolling version
     Existing Angular bindings preserved
============================================================ -->

<div class="view-area-root" [class.mobile-full-view]="isMobileFullView">

  <!-- ==========================================================
       PROPERTY DETAIL
       IMPORTANT:
       This component does NOT create its own vertical scroll.
       The parent search page/document handles scrolling.
  =========================================================== -->

  <main
    *ngIf="selectedItem"
    class="detail-scroll"
  >

    <div class="detail-content">

      <!-- ======================================================
           GALLERY
      ======================================================= -->

      <section class="gallery-surface">

        <app-gallery
          class="property-gallery"
          [images]="formattedGalleryImages"
          (lightboxOpen)="lightboxOpen.emit($event)">
        </app-gallery>

      </section>


      <!-- ======================================================
          PROPERTY SUMMARY
      ======================================================= -->

      <section class="property-summary">

        <span class="mobile-summary-updated">
          Last updated: {{ selectedItem.updatedAt | date:'dd MMM yyyy' }}
        </span>

        <!-- ====================================================
            CONTEXT / SELLER INFORMATION
        ===================================================== -->

        <!-- ====================================================
            PROPERTY HEADING
        ===================================================== -->

        <div class="property-heading-row">

          <!-- ==================================================
              PROPERTY INFORMATION
          =================================================== -->

          <div class="property-heading-copy">

            <!-- TITLE -->

            <p class="summary-eyebrow">PROPERTY DETAILS</p>
            <h1 class="property-title">
              {{ selectedItem.name | titlecase }}
            </h1>


            <!-- PRICE + LOCATION -->

            <div class="property-price-row">

              <span
                *ngIf="getPriceRange() as range"
                class="property-price"
              >

                {{ range.min | inrShort }}

                <ng-container *ngIf="!range.isSame">

                  <span class="price-dash">
                    -
                  </span>

                  {{ range.max | inrShort }}

                </ng-container>

              </span>


              <span
                *ngIf="selectedItem?.microMarket?.micromarketName"
                class="price-location-divider"
              >
                |
              </span>


              <span
                *ngIf="selectedItem?.microMarket?.micromarketName"
                class="property-micro-market"
              >
                {{ selectedItem.microMarket.micromarketName }}
              </span>

            </div>


            <!-- ==================================================
                PROPERTY TAGS
            =================================================== -->

            <div class="property-tags">

              <!-- BHK -->

              <span
                *ngFor="let item of getUniqueBhkTypes(selectedItem.price)"
                class="property-tag tag-purple"
              >
                {{ item }}
              </span>


              <!-- POSSESSION -->

              <span
                *ngIf="selectedItem?.possession"
                class="property-tag tag-neutral"
              >

                <svg class="tag-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M3 10 12 3l9 7v10H3V10Z" stroke-linejoin="round"/><path d="M9 20v-7h6v7"/></svg>

                {{ selectedItem.possession }}

              </span>


              <!-- PRIVATE TERRACE -->

              @if (
                hasAmenity(
                  selectedItem?.amenities,
                  'private_terrace'
                )
              ) {

                <span class="property-tag tag-blue">

                  <svg
                    class="tag-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >

                    <path
                      [attr.d]="AMENITY_MAP['private_terrace'].icon"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />

                  </svg>

                  {{ AMENITY_MAP['private_terrace'].label }}

                </span>

              }


              <!-- PRIVATE GARAGE -->

              @if (
                hasAmenity(
                  selectedItem?.amenities,
                  'private_garage'
                )
              ) {

                <span class="property-tag tag-blue">

                  <svg
                    class="tag-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >

                    <path
                      [attr.d]="AMENITY_MAP['private_garage'].icon"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />

                  </svg>

                  {{ AMENITY_MAP['private_garage'].label }}

                </span>

              }


              <!-- PRIVATE JACUZZI -->

              @if (
                hasAmenity(
                  selectedItem?.amenities,
                  'private_jacuzzi'
                )
              ) {

                <span class="property-tag tag-blue">

                  <svg
                    class="tag-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >

                    <path
                      [attr.d]="AMENITY_MAP['private_jacuzzi'].icon"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />

                  </svg>

                  {{ AMENITY_MAP['private_jacuzzi'].label }}

                </span>

              }

            </div>

          </div>


          <!-- ==================================================
              ACTIONS
          =================================================== -->

          <div class="property-actions">

            <div class="property-action-row">

              <!-- FAVORITE -->

              <button
                type="button"
                (click)="toggleFav(selectedItem._id)"
                [disabled]="togglingFavId === selectedItem._id"
                class="favorite-button"
                [ngClass]="_PS.isFav(selectedItem._id) ? 'is-favorite' : ''"
                [attr.aria-label]="_PS.isFav(selectedItem._id) ? 'Remove property from favourites' : 'Add property to favourites'"
                [attr.aria-pressed]="_PS.isFav(selectedItem._id)"
              >

                <!-- Loading -->

                <svg
                  *ngIf="togglingFavId === selectedItem._id"
                  class="favorite-icon animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >

                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />

                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />

                </svg>


                <!-- Filled -->

                <svg
                  *ngIf="
                    togglingFavId !== selectedItem._id &&
                    _PS.isFav(selectedItem._id)
                  "
                  class="favorite-icon"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >

                  <path
                    d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                  />

                </svg>


                <!-- Outline -->

                <svg
                  *ngIf="
                    togglingFavId !== selectedItem._id &&
                    !_PS.isFav(selectedItem._id)
                  "
                  class="favorite-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >

                  <path
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />

                </svg>

              </button>


              <!-- CONTACT ADVISOR -->

              <button
                type="button"
                *ngIf="!isMobile || isMobileFullView"
                (click)="propertyRequest(selectedItem._id)"
                [disabled]="isLoading"
                class="contact-advisor-button theme-btn1"
              >

                <span *ngIf="!isLoading">
                  Contact Advisor
                </span>


                <span
                  *ngIf="isLoading"
                  class="processing-state"
                >

                  <svg
                    class="processing-icon animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                  >

                    <circle
                      cx="12"
                      cy="12"
                      r="9"
                      stroke="currentColor"
                      stroke-width="2"
                      opacity=".3"
                    />

                    <path
                      d="M21 12a9 9 0 00-9-9"
                      stroke="currentColor"
                      stroke-width="2"
                    />

                  </svg>

                  Processing...

                </span>

              </button>

            </div>


            <!-- LAST UPDATED -->

            <span class="last-updated">
              Last updated:
              {{ selectedItem.updatedAt | date:'dd MMM yyyy' }}
            </span>

          </div>

        </div>

        <div class="context-chips">

          <span
            *ngIf="selectedItem?.communityCategory"
            class="context-chip"
          >
            <span class="context-label">
              Community
            </span>

            <strong>
              {{ selectedItem.communityCategory }}
            </strong>
          </span>


          <span
            *ngIf="selectedItem?.sellerProfile"
            class="context-chip"
          >
            <span class="context-label">
              Seller
            </span>

            <strong>
              {{ selectedItem.sellerProfile }}
            </strong>
          </span>

        </div>



      </section>


      <!-- ======================================================
          CIRCLED INTELLIGENCE (CI) SUMMARY
      ======================================================= -->

      @if (ciLoading) {
        <p class="ci-summary-description" role="status">Loading CI...</p>
      } @else if (ciReport) {
        <section class="ci-summary-grid">
          <article class="ci-summary-card ci-verdict-card">
            <div class="ci-summary-heading">
              <span class="ci-summary-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3 2.5 2 3.2.3.9 3.1L21 11l-1.4 2.9-.2 3.2-3.1.9L14 21l-2.9-1.4-3.2-.2-.9-3.1L3 14l1.4-2.9.2-3.2 3.1-.9L10 3Z"/><path d="m8 12 2.5 2.5L16 9"/></svg></span>
              <span>CIRCLED VERDICT</span>
            </div>
            <div class="ci-verdict-value">{{ ciReport.verdict || ciReport.tags.join(' · ') || 'Verdict not available' }}</div>
            <p class="ci-summary-description">{{ ciReport.summary }}</p>
          </article>
          <article class="ci-summary-card ci-savings-card">
            <div class="ci-summary-heading">
              <span class="ci-summary-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h7l9 9-7 7-9-9Z"/><circle cx="8" cy="8" r="1"/><path d="m11 16 5-5"/></svg></span>
              <span>NEGOTIATION POTENTIAL</span>
            </div>
            <div class="ci-summary-value">{{ ciReport.negotiationRating || 'Not available' }}</div>
            @if (ciReport.negotiationRating) { <p class="ci-summary-caption">Room to negotiate against the Circled assessment</p> }
          </article>
          @if (ciReport.propertyType !== 'Plots') {
            <article class="ci-summary-card ci-yield-card">
              <div class="ci-summary-heading">
                <span class="ci-summary-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20V4M4 20h16M8 15l4-4 3 2 5-7"/><path d="M15 6h5v5"/></svg></span>
                <span>RENTAL YIELD</span>
              </div>
              <div class="ci-summary-value">{{ headlineRentalYield }}</div>
              @if (headlineRentalYield !== 'Not available') { <p class="ci-summary-caption">Rental return on the asking price</p> }
            </article>
          }
        </section>
      } @else {
        <p class="ci-summary-description" role="status">{{ ciError || 'Circled Intelligence (CI) not available' }}</p>
      }


      <!-- ======================================================
           NAVIGATION TABS
      ======================================================= -->

      <section class="property-tabs-surface">

        <app-property-tabs
          [activeTab]="activeTab"
          (activeTabChange)="onTabChange($event)">
        </app-property-tabs>

      </section>


      <!-- ======================================================
           TAB CONTENT
      ======================================================= -->

      <section class="property-tab-content">

        @if (activeTab === 'property') {

          <app-property-overview
            [selectedItem]="selectedItem" [propertyAccessGranted]="propertyAccessGranted">
          </app-property-overview>

        }


        @if (activeTab === 'intelligence') {
          @if (canViewPropertyFeatures) {
          <app-property-intelligence [report]="ciReport" [loading]="ciLoading" [error]="ciError">
          </app-property-intelligence>
          } @else {
            <app-premium-feature-lock layout="intelligence" />
          }
        }


        @if (activeTab === 'investment') {
          @if (canViewPropertyFeatures) {
          <app-property-investment [report]="ciReport" [loading]="ciLoading" [error]="ciError">
          </app-property-investment>
          } @else {
            <app-premium-feature-lock layout="investment" />
          }
        }


        @if (activeTab === 'location') {

          <app-property-location
            [selectedItem]="selectedItem">
          </app-property-location>

        }

      </section>

    </div>

  </main>


  <!-- ==========================================================
       EMPTY STATE
  =========================================================== -->

  <main
    *ngIf="!selectedItem"
    class="empty-scroll"
  >

    <div class="empty-state">

      <div class="empty-icon">

        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-14 w-14"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>

      </div>


      <h3>
        No Properties Found
      </h3>


      <p>
        We couldn't find any properties matching your current filters.
        Try adjusting your budget or location.
      </p>

    </div>

  </main>


  <!-- ==========================================================
       SUCCESS MODAL
  =========================================================== -->

  <div
    *ngIf="showSuccessModal"
    class="fixed inset-0 z-[100] flex items-center justify-center p-4"
  >

    <div
      class="absolute inset-0 bg-black/50 backdrop-blur-sm"
      (click)="closeSuccessModal()"
    ></div>


    <div class="success-modal">

      <div class="success-icon">
        ✓
      </div>


      <p class="success-message">
        Thank you for your interest. An advisor will contact you soon
        on your registered mobile number.
      </p>


      <button
        type="button"
        (click)="closeSuccessModal()"
        class="success-button"
      >
        Okay
      </button>

    </div>

  </div>


  <!-- ==========================================================
       MOBILE CONTACT BAR
  =========================================================== -->

  <div
    *ngIf="isMobile && !isMobileFullView && selectedItem"
    class="mobile-contact-bar"
  >

    <button
      type="button"
      (click)="propertyRequest(selectedItem._id)"
      [disabled]="isLoading"
      class="mobile-contact-button theme-btn1"
    >

      <span *ngIf="!isLoading">
        Contact Advisor
      </span>


      <span
        *ngIf="isLoading"
        class="flex items-center justify-center gap-2"
      >

        <svg
          class="w-4 h-4 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
        >

          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            stroke-width="2"
            opacity=".3"
          />

          <path
            d="M21 12a9 9 0 00-9-9"
            stroke="currentColor"
            stroke-width="2"
          />

        </svg>

        Processing...

      </span>

    </button>

  </div>

</div>
